<template>
  <div class="chart-card">
    <div class="chart-header">
      <div>
        <h2 class="chart-title">
          Gifts by {{ dataSelectChartValue.name }}
        </h2>
        <p class="chart-subtitle">
          {{ dateRangeParsed }}
        </p>
      </div>
      <div class="dropdown">
        <v-select
          v-model="dataSelectChartValue"
          :options="dataSelectOptionsChart"
          label="name"
          placeholder="Day"
          :clearable="false"
          :class="[
            'call-select'
          ]"
          @input="$emit('dataSelectChartValue', dataSelectChartValue)">
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes">
              <font-awesome-icon :icon="dataIconDown" />
            </span>
          </template>
          <template
            slot="option"
            slot-scope="option">
            <div class="d-center">
              {{ option.name }}
            </div>
          </template>
        </v-select>
      </div>
    </div>
    <div
      class="chart-body"
      :style="{ height: chartHeight + 'px' }">
      <canvas ref="barChart" />
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';
import moment from 'moment';
import selectDepositsPeriod from '@/mixins/select/selectDepositsPeriod';

const BONUS_COLORS = [
  '#20a052',
  '#2196F3',
  '#FF9800',
  '#9C27B0',
  '#F44336',
  '#00BCD4',
  '#FFEB3B',
  '#795548',
  '#607D8B',
  '#E91E63',
  '#3F51B5',
  '#8BC34A',
  '#FF5722',
  '#009688',
  '#CDDC39',
  '#4CAF50',
  '#673AB7',
  '#FFC107',
  '#03A9F4',
  '#795548'
];

export default {
  name: 'BonusesChart',
  mixins: [selectDepositsPeriod],
  props: {
    bonusesByPeriod: {
      type: Array,
      required: true
    },
    formattedRange: {
      type: Array,
      required: true
    },
    bonusLabels: {
      type: Object,
      default: () => ({})
    },
    dataSelectChartValueId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      chart: null,
      dataSelectOptionsChart: [
        { id: 'day', name: 'Day' },
        { id: 'week', name: 'Week' },
        { id: 'month', name: 'Month' }
      ],
      dataSelectChartValue: { id: 'day', name: 'Day' }
    };
  },

  computed: {
    dateRangeParsed () {
      return `${moment(this.formattedRange[0]).format(
        'D MMMM YYYY'
      )} - ${moment(this.formattedRange[1]).format('D MMMM YYYY')}`;
    },
    // Ключи датасетов: все ключи из первого элемента, кроме stats_date и bonuses_count
    breakdownKeys () {
      if (!this.bonusesByPeriod || !this.bonusesByPeriod.length) return [];
      const first = this.bonusesByPeriod[0];
      return Object.keys(first).filter(
        (k) => k !== 'stats_date' && k !== 'bonuses_count'
      ).sort((a, b) => {
        // Сортируем по числовому ID если это bonus_N
        const numA = parseInt(a.replace('bonus_', ''), 10);
        const numB = parseInt(b.replace('bonus_', ''), 10);
        return (isNaN(numA) ? 0 : numA) - (isNaN(numB) ? 0 : numB);
      });
    },
    isStacked () {
      return this.breakdownKeys.length > 0;
    },
    chartHeight () {
      const baseHeight = 300;
      const extraPerBonus = 20;
      const threshold = 4;
      // Высота зависит только от количества breakdown-ключей в grouped_by_period
      const breakdownCount = this.breakdownKeys.length;

      if (breakdownCount > threshold) {
        return baseHeight + (breakdownCount - threshold) * extraPerBonus;
      }
      return baseHeight;
    }
  },

  watch: {
    bonusesByPeriod: {
      handler () {
        this.renderChart();
      },
      deep: true
    },
    breakdownKeys () {
      // Высота контейнера меняется через computed chartHeight - пересчитываем график
      this.$nextTick(() => {
        if (this.chart) this.chart.resize();
      });
    }
  },

  mounted () {
    this.dataSelectChartValue = this.dataSelectOptionsChart.find(el => el.id === this.dataSelectChartValueId);
    this.renderChart();
  },

  beforeDestroy () {
    if (this.chart) this.chart.destroy();
  },

  methods: {
    renderChart () {
      if (this.chart) this.chart.destroy();

      const labels = this.bonusesByPeriod.map(item => item.stats_date);
      const stacked = this.isStacked;
      let datasets;

      if (stacked) {
        // Есть breakdown по типам бонусов — строим stacked
        datasets = this.breakdownKeys.map((key, idx) => ({
          label: this.bonusLabels[key] || key,
          data: this.bonusesByPeriod.map(item => item[key] || 0),
          backgroundColor: BONUS_COLORS[idx % BONUS_COLORS.length]
        }));
      } else {
        // Только bonuses_count — простой bar
        datasets = [
          {
            label: 'Gifts',
            data: this.bonusesByPeriod.map(item => item.bonuses_count),
            backgroundColor: '#20a052'
          }
        ];
      }

      const ctx = this.$refs.barChart.getContext('2d');

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: {
            duration: 800,
            easing: 'easeOutQuart'
          },
          legend: { display: stacked, position: 'top' },
          scales: {
            yAxes: [
              {
                display: true,
                stacked: stacked,
                ticks: {
                  fontSize: 12,
                  fontColor: '#333',
                  beginAtZero: true,
                  stepSize: 1,
                  maxTicksLimit: 6
                }
              }
            ],
            xAxes: [
              {
                stacked: stacked,
                gridLines: { display: false },
                ticks: {
                  fontColor: '#333',
                  fontSize: 12
                }
              }
            ]
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            displayColors: stacked,
            callbacks: {
              title: (tooltipItem, data) => {
                const index = tooltipItem[0].index;
                if (!stacked) {
                  return `Gifts: ${data.datasets[0].data[index]}`;
                }
                let total = 0;
                data.datasets.forEach((ds) => {
                  total += ds.data[index] || 0;
                });
                return `Total Gifts: ${total}`;
              },
              label: (tooltipItem, data) => {
                if (!stacked) return '';
                const index = tooltipItem.index;
                const datasetIndex = tooltipItem.datasetIndex;
                const datasetLabel = data.datasets[datasetIndex].label;
                const value = tooltipItem.yLabel;

                let total = 0;
                data.datasets.forEach((ds) => {
                  total += ds.data[index] || 0;
                });

                const percent = total > 0
                  ? ((value / total) * 100).toFixed(1)
                  : 0;

                return `${datasetLabel}: ${value} (${percent}%)`;
              }
            }
          },
          hover: {
            mode: 'index',
            intersect: false
          }
        }
      });
    }
  }
};
</script>

<style lang="scss">
.chart-card {
  .vs__selected-options {
    min-width: 60px !important;
    > * {
      margin: 2px;
    }
    .vs__actions {
      padding: 2px;
    }
  }
}
</style>

<style scoped lang="scss">
.chart-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  max-width: 100%;
  // canvas {
  //   // height: 200px !important;
  // }
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 20px;
  padding-bottom: 0;
}

.chart-body {
  padding: 20px;
  position: relative;
}

.chart-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

.chart-subtitle {
  font-size: 14px;
  color: #666;
  margin: 2px 0 0 0;
}
</style>
